import type { DrawingEntity } from '../contract/types.js';
import type { BinaryImage } from './threshold.js';

export interface DetectedLine {
  kind: 'line';
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** 0 to 1 inclusive. */
  confidence: number;
}

export interface DetectedCircle {
  kind: 'circle';
  cx: number;
  cy: number;
  r: number;
  confidence: number;
}

/**
 * Several traced edges recognised as the one shape they describe.
 *
 * A drawing frame reaches the detector as four separate Hough peaks, and ground
 * truth records it as a single closed LWPOLYLINE. Reporting the four is not a
 * near miss, it is four false positives and a missing polyline. `rectangles.ts`
 * is the assembly that closes that gap; this is what it produces.
 *
 * Vertices are in the detector's own pixel frame, like every other detection,
 * and are converted by `toEntities` alone.
 */
export interface DetectedPolyline {
  kind: 'polyline';
  vertices: readonly { readonly x: number; readonly y: number }[];
  closed: boolean;
  confidence: number;
}

export type Detection = DetectedLine | DetectedCircle | DetectedPolyline;

export interface DetectorContext {
  /** From the raster manifest, or from the DPI the page was rendered at. */
  readonly pixelsPerPoint: number;
  /**
   * The page extent this image covers, in points, carried so a consumer can
   * report and bound what was found.
   *
   * It is deliberately **not** used to flip the y axis. See `toEntities`.
   */
  readonly pageHeightPt: number;
}

/**
 * One interface, three implementations.
 *
 * A fake so the merge and the acceptance runner can be tested without a detector
 * at all; a pure-TypeScript baseline that ships in the browser; and OpenCV,
 * measured in the kernel and never deployed. The port is what makes the accuracy
 * claim checkable: not "the raster pipeline scores X" — which nobody can verify —
 * but "this named detector scores X on this corpus at this DPI".
 */
export interface Detector {
  readonly name: string;
  detect(image: BinaryImage, context: DetectorContext): readonly Detection[];
}

export function fakeDetector(detections: readonly Detection[]): Detector {
  return { name: 'fake', detect: () => detections };
}

/**
 * Detections into contract entities, in page points.
 *
 * **There is no y flip, and that is measured rather than assumed.** A raster row
 * index grows downward, and so does this contract's page space: ground truth
 * anchors "DRAWING No. A-001" at `[60, 70]` and PyMuPDF's own `search_for` finds
 * that string at `Rect(60.0, 60.33, 146.5, 72.69)`, near the top of the sheet.
 * `rotate_ground_truth` in `kernel/fixtures/generate.py` documents the same frame
 * — "top-left origin with y increasing downward" — and it is the frame the vector
 * pipeline already scores 1.000 against. The two frames agree, so the conversion
 * is a pure scale.
 *
 * Subtracting from the page height instead would mirror every detection about the
 * page's horizontal centreline. That is the expensive kind of wrong: the drawing
 * still looks like a drawing, and it scores zero.
 *
 * Every entity carries its confidence: the contract says `confidence` is "present
 * only for raster-derived or merged entities", so a raster entity without one is
 * claiming to be vector.
 */
export function toEntities(
  detections: readonly Detection[],
  context: DetectorContext,
): DrawingEntity[] {
  const { pixelsPerPoint: scale } = context;
  const toPoint = (x: number, y: number): { x: number; y: number } => ({
    x: x / scale,
    y: y / scale,
  });

  return detections.map((detection, index) => {
    checkConfidence(detection.confidence, index);
    // Index-based and therefore stable for a given detection list, which is what
    // determinism needs. Prefixed so it cannot collide with a vector identifier.
    const id = `r${String(index).padStart(4, '0')}`;
    const base = {
      id,
      layer: 'raster',
      strokeWidth: 0.25,
      colour: '000000',
      origin: 'raster' as const,
      confidence: detection.confidence,
    };

    if (detection.kind === 'line') {
      const start = toPoint(detection.x1, detection.y1);
      const end = toPoint(detection.x2, detection.y2);
      return {
        ...base,
        type: 'LINE',
        start,
        end,
        source: {
          pageIndex: 0,
          operatorIndex: -1,
          bbox: [
            Math.min(start.x, end.x),
            Math.min(start.y, end.y),
            Math.max(start.x, end.x),
            Math.max(start.y, end.y),
          ] as const,
        },
      };
    }

    if (detection.kind === 'polyline') {
      checkVertices(detection.vertices.length, index);
      const vertices = detection.vertices.map((v) => toPoint(v.x, v.y));
      // One bulge per vertex, and all of them zero, because a bulge is the
      // tangent of a quarter of an arc's included angle and every edge assembled
      // from traced straight segments is straight. Omitting the array would be a
      // contract violation; inventing a bulge would be a drawing that is not the
      // one on the paper.
      return {
        ...base,
        type: 'LWPOLYLINE',
        vertices,
        closed: detection.closed,
        bulges: vertices.map(() => 0),
        source: {
          pageIndex: 0,
          operatorIndex: -1,
          bbox: [
            Math.min(...vertices.map((v) => v.x)),
            Math.min(...vertices.map((v) => v.y)),
            Math.max(...vertices.map((v) => v.x)),
            Math.max(...vertices.map((v) => v.y)),
          ] as const,
        },
      };
    }

    const centre = toPoint(detection.cx, detection.cy);
    const radius = detection.r / scale;
    return {
      ...base,
      type: 'CIRCLE',
      centre,
      radius,
      source: {
        pageIndex: 0,
        operatorIndex: -1,
        bbox: [centre.x - radius, centre.y - radius, centre.x + radius, centre.y + radius] as const,
      },
    };
  });
}

/**
 * A polyline needs two vertices before it is anywhere at all.
 *
 * Without this the bounding box of an empty vertex list is
 * `[Infinity, Infinity, -Infinity, -Infinity]` — an entity that claims to be
 * everywhere and nowhere, which every consumer of `source.bbox` would then have
 * to defend against.
 */
function checkVertices(count: number, index: number): void {
  if (count < 2) {
    throw new Error(`detection ${index} is a polyline with ${String(count)} vertices; it needs 2`);
  }
}

function checkConfidence(confidence: number, index: number): void {
  if (!Number.isFinite(confidence) || confidence < 0 || confidence > 1) {
    throw new Error(
      `detection ${index} has confidence ${String(confidence)}; it must be a number from 0 to 1`,
    );
  }
}
